import { ChevronRight } from 'lucide-react';
import { useId, useState } from 'react';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { cardSurface } from '../primitives/card.tsx';
import { IconTile } from './IconTile.tsx';
import type { IconTileTone } from './IconTile.tsx';

export interface PanelProps extends Omit<ComponentProps<'section'>, 'title'> {
  /** Which part of the page this is. Never repeat the page title here. */
  title?: ReactNode;
  /** A count, or a caveat that changes what you do next. Not a description. */
  sub?: ReactNode;
  /** Leading icon in the header, drawn in a small tinted tile. */
  icon?: ReactNode;
  /** The tile's tint (default accent). */
  iconTone?: IconTileTone;
  /** Trailing header controls (compact: IconButton, small Button, Segmented). */
  actions?: ReactNode;
  /** Body without padding (lists that run edge to edge). */
  flush?: boolean;
  /**
   * surface (default): a panel on the canvas, grouped by surface and space, no lines inside.
   * plain: the same header and rhythm straight on the canvas, for a section that needs no box.
   */
  variant?: 'surface' | 'plain';
  bodyClassName?: string;
  /**
   * A secondary section that folds (progressive disclosure): the header becomes the toggle, with
   * a chevron, and the body shows only while open. Closed by default unless `defaultOpen`.
   */
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** Controlled open state, for a collapsible panel that opens itself (an error inside it). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * A titled section. The header is part of the surface, not a strip above a line: a 48 px row
 * with the title, a quiet count and the trailing controls, then the body. Rows inside group by
 * rhythm and the hover fill, never by a rule under each one (colour.md, "Depth").
 */
/** A bare count ("9", "1,204"), set in the title's size; anything else is a summary line. */
const isCount = (v: ReactNode): boolean => typeof v === 'number' || (typeof v === 'string' && /^[\d,.]+$/.test(v));

export function Panel({
  title,
  sub,
  icon,
  iconTone,
  actions,
  flush,
  variant = 'surface',
  className,
  bodyClassName,
  collapsible,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  children,
  ...rest
}: PanelProps) {
  const hasHeader = title != null || actions != null;
  const [openState, setOpenState] = useState(defaultOpen);
  const open = !collapsible || (openProp ?? openState);
  const bodyId = useId();
  const toggle = () => {
    const next = !open;
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };
  const heading = (
    <>
      {icon && <IconTile icon={icon} tone={iconTone} size="sm" className="mr-1" />}
      {title != null && <h2 className="truncate text-lead text-fg">{title}</h2>}
      {/* flex-1 from a zero basis: on a narrow panel the sub gives way first, so the title stays readable.
          A bare count sits in the title's size at 400 ("Live now 9"); a summary line is ui size, so it fits. */}
      {sub != null && <span className={cn('min-w-0 flex-1 truncate font-normal text-fg-subtle num', isCount(sub) ? 'text-lead' : 'text-ui')}>{sub}</span>}
    </>
  );
  return (
    <section data-slot="panel" className={cn(variant === 'surface' && cardSurface, 'flex min-w-0 flex-col', className)} {...rest}>
      {hasHeader && (
        <header className={cn('flex h-12 shrink-0 items-center gap-2 pr-2', variant === 'surface' ? 'pl-4' : 'pl-0')}>
          {collapsible ? (
            <button
              type="button"
              aria-expanded={open}
              aria-controls={bodyId}
              onClick={toggle}
              className="group flex h-full min-w-0 flex-1 items-center gap-2 rounded-control text-left -outline-offset-2"
            >
              {heading}
              <ChevronRight
                aria-hidden
                className={cn('ml-auto size-4 shrink-0 text-fg-subtle transition-transform duration-(--duration-fast) group-hover:text-fg-muted', open && 'rotate-90')}
              />
            </button>
          ) : (
            heading
          )}
          <span className={cn('flex items-center gap-1', !collapsible && 'ml-auto')}>{actions}</span>
        </header>
      )}
      {open && (
        <div
          id={collapsible ? bodyId : undefined}
          className={cn('min-h-0 flex-1', !flush && (hasHeader ? 'px-(--space-inset-card) pb-(--space-inset-card)' : 'p-(--space-inset-card)'), flush && hasHeader && 'pb-1', bodyClassName)}
        >
          {children}
        </div>
      )}
    </section>
  );
}

export interface SectionHeaderProps {
  title: ReactNode;
  /** A live count beside the title. */
  count?: ReactNode;
  /** One quiet fact after the count: "oldest 14m". */
  meta?: ReactNode;
  icon?: ReactNode;
  actions?: ReactNode;
  /** The heading level; h2 by default (the top bar holds the page's h1). */
  as?: 'h2' | 'h3';
  className?: string;
}

/** A section's title on the canvas, above its content: title, count, one fact, trailing controls. */
export function SectionHeader({ title, count, meta, icon, actions, as: H = 'h2', className }: SectionHeaderProps) {
  return (
    <div className={cn('flex min-h-8 min-w-0 items-center gap-2', className)}>
      {icon && <span className="flex text-fg-subtle [&_svg]:size-4">{icon}</span>}
      <H className="truncate text-body font-medium text-fg">{title}</H>
      {count != null && <span className="text-body text-fg-subtle num">{count}</span>}
      {meta != null && <span className="min-w-0 truncate text-caption text-fg-subtle">{meta}</span>}
      {actions != null && <span className="ml-auto flex shrink-0 items-center gap-1">{actions}</span>}
    </div>
  );
}

export interface PageHeaderProps {
  title: ReactNode;
  /** One line under the title: what this is, or the live summary ("15 agents working across 4 projects"). */
  description?: ReactNode;
  /** The page's controls; at most one primary. */
  actions?: ReactNode;
  className?: string;
}

/**
 * A heading inside the page body, for a page whose top-bar title needs a summary line under it
 * (the top bar keeps the page's h1, so this is an h2).
 */
export function PageHeader({ title, description, actions, className }: PageHeaderProps) {
  return (
    <div className={cn('flex min-w-0 flex-wrap items-end justify-between gap-x-6 gap-y-3', className)}>
      <div className="flex min-w-0 flex-col gap-1">
        <h2 className="font-display text-heading text-fg">{title}</h2>
        {description != null && <p className="max-w-(--layout-width-prose) text-body text-fg-subtle">{description}</p>}
      </div>
      {actions != null && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}
